import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Palette, Sun, Moon, Monitor, Plus, Edit2, Eye } from 'lucide-react';

const Themes = () => {
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTheme, setEditingTheme] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewTheme, setPreviewTheme] = useState(null);
  const [formData, setFormData] = useState({
    theme_code: '',
    theme_name: '',
    primary_color: '#3B82F6',
    secondary_color: '#8B5CF6',
    accent_color: '#10B981',
    background_light: '#FFFFFF',
    background_dark: '#1F2937',
    surface_light: '#F9FAFB',
    surface_dark: '#374151',
    text_primary_light: '#111827',
    text_primary_dark: '#F9FAFB',
    text_secondary_light: '#6B7280',
    text_secondary_dark: '#D1D5DB',
    success_color: '#10B981',
    warning_color: '#F59E0B',
    error_color: '#EF4444',
    is_default_dark: false,
  });

  useEffect(() => {
    loadThemes();
  }, []);

  const loadThemes = async () => {
    try {
      const response = await api.getThemes();
      setThemes(response.data);
    } catch (error) {
      console.error('Error loading themes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.createTheme(formData);
      setShowForm(false);
      setEditingTheme(null);
      resetForm();
      loadThemes();
    } catch (error) {
      console.error('Error creating theme:', error);
      alert('Failed to create theme');
    }
  };

  const resetForm = () => {
    setFormData({
      theme_code: '',
      theme_name: '',
      primary_color: '#3B82F6',
      secondary_color: '#8B5CF6',
      accent_color: '#10B981',
      background_light: '#FFFFFF',
      background_dark: '#1F2937',
      surface_light: '#F9FAFB',
      surface_dark: '#374151',
      text_primary_light: '#111827',
      text_primary_dark: '#F9FAFB',
      text_secondary_light: '#6B7280',
      text_secondary_dark: '#D1D5DB',
      success_color: '#10B981',
      warning_color: '#F59E0B',
      error_color: '#EF4444',
      is_default_dark: false,
    });
  };

  const handlePreview = (theme) => {
    setPreviewTheme(theme);
    setShowPreview(true);
  };

  const getThemeModeIcon = (mode) => {
    if (mode === 'LIGHT') return <Sun size={16} />;
    if (mode === 'DARK') return <Moon size={16} />;
    return <Monitor size={16} />;
  };

  return (
    <div className="p-8" data-testid="themes-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Themes</h1>
          <p className="text-gray-600 mt-2">Manage UI themes for outlets</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
          data-testid="create-theme-btn"
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
        >
          <Plus size={20} />
          Create Theme
        </button>
      </div>

      {/* Create/Edit Theme Form */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl my-8" data-testid="theme-form">
            <h2 className="text-xl font-bold mb-4">{editingTheme ? 'Edit Theme' : 'Create Theme'}</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Theme Code *
                  </label>
                  <input
                    type="text"
                    required
                    data-testid="theme-code-input"
                    value={formData.theme_code}
                    onChange={(e) => setFormData({ ...formData, theme_code: e.target.value.toUpperCase() })}
                    placeholder="e.g., CUSTOM_LIGHT"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Theme Name *
                  </label>
                  <input
                    type="text"
                    required
                    data-testid="theme-name-input"
                    value={formData.theme_name}
                    onChange={(e) => setFormData({ ...formData, theme_name: e.target.value })}
                    placeholder="e.g., Custom Light Theme"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              {/* Primary Colors */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Primary Colors</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Primary</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.primary_color}
                        onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                        className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.primary_color}
                        onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Secondary</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.secondary_color}
                        onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                        className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.secondary_color}
                        onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Accent</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.accent_color}
                        onChange={(e) => setFormData({ ...formData, accent_color: e.target.value })}
                        className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.accent_color}
                        onChange={(e) => setFormData({ ...formData, accent_color: e.target.value })}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Status Colors */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Status Colors</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Success</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.success_color}
                        onChange={(e) => setFormData({ ...formData, success_color: e.target.value })}
                        className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.success_color}
                        onChange={(e) => setFormData({ ...formData, success_color: e.target.value })}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Warning</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.warning_color}
                        onChange={(e) => setFormData({ ...formData, warning_color: e.target.value })}
                        className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.warning_color}
                        onChange={(e) => setFormData({ ...formData, warning_color: e.target.value })}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Error</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.error_color}
                        onChange={(e) => setFormData({ ...formData, error_color: e.target.value })}
                        className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.error_color}
                        onChange={(e) => setFormData({ ...formData, error_color: e.target.value })}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Background Colors */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Background Colors</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Light Mode Background</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.background_light}
                        onChange={(e) => setFormData({ ...formData, background_light: e.target.value })}
                        className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.background_light}
                        onChange={(e) => setFormData({ ...formData, background_light: e.target.value })}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Dark Mode Background</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.background_dark}
                        onChange={(e) => setFormData({ ...formData, background_dark: e.target.value })}
                        className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.background_dark}
                        onChange={(e) => setFormData({ ...formData, background_dark: e.target.value })}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Light Mode Surface</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.surface_light}
                        onChange={(e) => setFormData({ ...formData, surface_light: e.target.value })}
                        className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.surface_light}
                        onChange={(e) => setFormData({ ...formData, surface_light: e.target.value })}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Dark Mode Surface</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.surface_dark}
                        onChange={(e) => setFormData({ ...formData, surface_dark: e.target.value })}
                        className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.surface_dark}
                        onChange={(e) => setFormData({ ...formData, surface_dark: e.target.value })}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Text Colors */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Text Colors</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Light Mode Primary Text</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.text_primary_light}
                        onChange={(e) => setFormData({ ...formData, text_primary_light: e.target.value })}
                        className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.text_primary_light}
                        onChange={(e) => setFormData({ ...formData, text_primary_light: e.target.value })}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Dark Mode Primary Text</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.text_primary_dark}
                        onChange={(e) => setFormData({ ...formData, text_primary_dark: e.target.value })}
                        className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.text_primary_dark}
                        onChange={(e) => setFormData({ ...formData, text_primary_dark: e.target.value })}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Light Mode Secondary Text</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.text_secondary_light}
                        onChange={(e) => setFormData({ ...formData, text_secondary_light: e.target.value })}
                        className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.text_secondary_light}
                        onChange={(e) => setFormData({ ...formData, text_secondary_light: e.target.value })}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Dark Mode Secondary Text</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.text_secondary_dark}
                        onChange={(e) => setFormData({ ...formData, text_secondary_dark: e.target.value })}
                        className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.text_secondary_dark}
                        onChange={(e) => setFormData({ ...formData, text_secondary_dark: e.target.value })}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Default Dark Mode */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_default_dark"
                  checked={formData.is_default_dark}
                  onChange={(e) => setFormData({ ...formData, is_default_dark: e.target.checked })}
                  className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                />
                <label htmlFor="is_default_dark" className="text-sm font-medium text-gray-700">
                  This is a dark theme by default
                </label>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => handlePreview(formData)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center gap-2"
                >
                  <Eye size={18} />
                  Preview
                </button>
                <div className="flex-1"></div>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingTheme(null);
                    resetForm();
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="submit-theme"
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  {editingTheme ? 'Update' : 'Create'} Theme
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && previewTheme && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-3xl" data-testid="theme-preview">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Theme Preview</h2>
              <button
                onClick={() => setShowPreview(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                ✕
              </button>
            </div>
            <div className="space-y-4">
              {/* Light Mode Preview */}
              <div>
                <h3 className="font-semibold mb-2">Light Mode</h3>
                <div 
                  className="p-6 rounded-lg border-2"
                  style={{ 
                    backgroundColor: previewTheme.background_light,
                    color: previewTheme.text_primary_light 
                  }}
                >
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <button 
                        className="px-4 py-2 rounded text-white font-medium"
                        style={{ backgroundColor: previewTheme.primary_color }}
                      >
                        Primary Button
                      </button>
                      <button 
                        className="px-4 py-2 rounded text-white font-medium"
                        style={{ backgroundColor: previewTheme.secondary_color }}
                      >
                        Secondary Button
                      </button>
                      <button 
                        className="px-4 py-2 rounded text-white font-medium"
                        style={{ backgroundColor: previewTheme.accent_color }}
                      >
                        Accent Button
                      </button>
                    </div>
                    <div 
                      className="p-4 rounded"
                      style={{ backgroundColor: previewTheme.surface_light }}
                    >
                      <p style={{ color: previewTheme.text_primary_light }}>Primary text on surface</p>
                      <p style={{ color: previewTheme.text_secondary_light }}>Secondary text on surface</p>
                    </div>
                    <div className="flex gap-2">
                      <div 
                        className="px-3 py-2 rounded text-white text-sm"
                        style={{ backgroundColor: previewTheme.success_color }}
                      >
                        Success
                      </div>
                      <div 
                        className="px-3 py-2 rounded text-white text-sm"
                        style={{ backgroundColor: previewTheme.warning_color }}
                      >
                        Warning
                      </div>
                      <div 
                        className="px-3 py-2 rounded text-white text-sm"
                        style={{ backgroundColor: previewTheme.error_color }}
                      >
                        Error
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Dark Mode Preview */}
              <div>
                <h3 className="font-semibold mb-2">Dark Mode</h3>
                <div 
                  className="p-6 rounded-lg border-2"
                  style={{ 
                    backgroundColor: previewTheme.background_dark,
                    color: previewTheme.text_primary_dark 
                  }}
                >
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <button 
                        className="px-4 py-2 rounded text-white font-medium"
                        style={{ backgroundColor: previewTheme.primary_color }}
                      >
                        Primary Button
                      </button>
                      <button 
                        className="px-4 py-2 rounded text-white font-medium"
                        style={{ backgroundColor: previewTheme.secondary_color }}
                      >
                        Secondary Button
                      </button>
                      <button 
                        className="px-4 py-2 rounded text-white font-medium"
                        style={{ backgroundColor: previewTheme.accent_color }}
                      >
                        Accent Button
                      </button>
                    </div>
                    <div 
                      className="p-4 rounded"
                      style={{ backgroundColor: previewTheme.surface_dark }}
                    >
                      <p style={{ color: previewTheme.text_primary_dark }}>Primary text on surface</p>
                      <p style={{ color: previewTheme.text_secondary_dark }}>Secondary text on surface</p>
                    </div>
                    <div className="flex gap-2">
                      <div 
                        className="px-3 py-2 rounded text-white text-sm"
                        style={{ backgroundColor: previewTheme.success_color }}
                      >
                        Success
                      </div>
                      <div 
                        className="px-3 py-2 rounded text-white text-sm"
                        style={{ backgroundColor: previewTheme.warning_color }}
                      >
                        Warning
                      </div>
                      <div 
                        className="px-3 py-2 rounded text-white text-sm"
                        style={{ backgroundColor: previewTheme.error_color }}
                      >
                        Error
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {themes.map((theme) => (
            <div
              key={theme.theme_id}
              data-testid={`theme-card-${theme.theme_code}`}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="bg-purple-100 p-3 rounded-lg">
                  <Palette className="text-purple-600" size={24} />
                </div>
                <span
                  className={`px-2 py-1 rounded text-xs ${
                    theme.is_default_dark
                      ? 'bg-gray-800 text-white'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {theme.is_default_dark ? 'Dark Default' : 'Light Default'}
                </span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">{theme.theme_name}</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-1">Code</p>
                  <p className="text-sm text-gray-900">{theme.theme_code}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-2">Color Palette</p>
                  <div className="grid grid-cols-3 gap-2">
                    <div
                      className="h-10 rounded border border-gray-200"
                      style={{ backgroundColor: theme.primary_color }}
                      title="Primary"
                    ></div>
                    <div
                      className="h-10 rounded border border-gray-200"
                      style={{ backgroundColor: theme.secondary_color }}
                      title="Secondary"
                    ></div>
                    <div
                      className="h-10 rounded border border-gray-200"
                      style={{ backgroundColor: theme.accent_color }}
                      title="Accent"
                    ></div>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-2">Status Colors</p>
                  <div className="flex gap-2">
                    <div
                      className="h-8 flex-1 rounded border border-gray-200"
                      style={{ backgroundColor: theme.success_color }}
                      title="Success"
                    ></div>
                    <div
                      className="h-8 flex-1 rounded border border-gray-200"
                      style={{ backgroundColor: theme.warning_color }}
                      title="Warning"
                    ></div>
                    <div
                      className="h-8 flex-1 rounded border border-gray-200"
                      style={{ backgroundColor: theme.error_color }}
                      title="Error"
                    ></div>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-2">Backgrounds</p>
                  <div className="flex gap-2">
                    <div
                      className="h-8 flex-1 rounded border border-gray-300"
                      style={{ backgroundColor: theme.background_light }}
                      title="Light Background"
                    ></div>
                    <div
                      className="h-8 flex-1 rounded border border-gray-300"
                      style={{ backgroundColor: theme.background_dark }}
                      title="Dark Background"
                    ></div>
                  </div>
                </div>
              </div>
              <button
                onClick={() => handlePreview(theme)}
                data-testid={`preview-theme-${theme.theme_code}`}
                className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
              >
                <Eye size={16} />
                Preview Theme
              </button>
            </div>
          ))}
        </div>
      )}

      {themes.length === 0 && !loading && (
        <div className="text-center py-12">
          <Palette className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No themes</h3>
          <p className="mt-1 text-sm text-gray-500">Click "Create Theme" to get started.</p>
        </div>
      )}

      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">💡 How to Use Themes</h3>
        <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
          <li>Create a new theme with your desired colors</li>
          <li>Preview the theme in both light and dark modes</li>
          <li>Go to the Outlets page</li>
          <li>Click "Change Theme" button on any outlet card</li>
          <li>Select your theme and mode (Light/Dark/Auto)</li>
        </ol>
      </div>
    </div>
  );
};

export default Themes;
